import { useEffect, useState, type FormEvent, type ReactNode } from "react";

export const REPORT_REASON_LABELS = {
  harassment: "嫌がらせ・いじめ",
  hate: "差別・ヘイト",
  violence: "暴力・脅迫",
  sexual: "性的な内容",
  self_harm: "自傷・自殺に関する危険",
  misinformation: "誤解を招く情報",
  spam: "スパム・宣伝",
  privacy: "個人情報・プライバシー",
  impersonation: "なりすまし",
  other: "その他",
} as const;

export type ReportReasonKey = keyof typeof REPORT_REASON_LABELS;
export type ReportTargetType = "user" | "question" | "answer";
export type ReportSubmission = { targetType: ReportTargetType; targetId: string; reason: ReportReasonKey; details: string };

type ReportDialogProps = {
  open: boolean;
  targetType: ReportTargetType;
  targetId: string;
  targetLabel: string;
  onSubmit: (report: ReportSubmission) => Promise<void>;
  onClose: () => void;
  challenge?: ReactNode;
};

export function ReportDialog({ open, targetType, targetId, targetLabel, onSubmit, onClose, challenge }: ReportDialogProps) {
  const [reason, setReason] = useState<ReportReasonKey>("harassment");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("harassment");
    setDetails("");
    setError("");
  }, [open, targetId]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onSubmit({ targetType, targetId, reason, details: details.trim() });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "通報を送信できませんでした");
    } finally {
      setBusy(false);
    }
  }

  return <div className="safety-dialog-backdrop" role="presentation" onMouseDown={(event) => {
    if (event.target === event.currentTarget && !busy) onClose();
  }}>
    <section className="safety-dialog" role="dialog" aria-modal="true" aria-labelledby="report-dialog-title">
      <header>
        <div><span className="safety-eyebrow">安全のために</span><h2 id="report-dialog-title">通報する</h2></div>
        <button type="button" className="safety-close" aria-label="閉じる" onClick={onClose} disabled={busy}>×</button>
      </header>
      <p className="safety-target">対象：{targetLabel}</p>
      <form onSubmit={submit}>
        <label>理由
          <select value={reason} onChange={(event) => setReason(event.target.value as ReportReasonKey)} disabled={busy}>
            {Object.entries(REPORT_REASON_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>補足（任意）
          <textarea value={details} onChange={(event) => setDetails(event.target.value)} maxLength={1000} rows={4} disabled={busy} placeholder="状況が分かる情報だけを記入してください。新たな個人情報は書かないでください。" />
          <small>{details.length} / 1000</small>
        </label>
        {challenge}
        {error && <p className="safety-error" role="alert">{error}</p>}
        <p className="safety-fine-print">通報対象の内容は、確認時点の記録として安全管理者に送られます。</p>
        <div className="safety-dialog-actions">
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>キャンセル</button>
          <button type="submit" className="danger" disabled={busy}>{busy ? "送信中…" : "通報を送信"}</button>
        </div>
      </form>
    </section>
  </div>;
}

type BlockButtonProps = {
  userId: string;
  userName: string;
  blocked: boolean;
  onBlock: (userId: string) => Promise<void>;
  onUnblock: (userId: string) => Promise<void>;
};

export function BlockButton({ userId, userName, blocked, onBlock, onUnblock }: BlockButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggle() {
    const confirmation = blocked
      ? `${userName}さんのブロックを解除しますか？`
      : `${userName}さんをブロックしますか？\nお互いの問い・回答・プロフィールが表示されなくなります。`;
    if (!window.confirm(confirmation)) return;
    setBusy(true);
    setError("");
    try {
      if (blocked) await onUnblock(userId);
      else await onBlock(userId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "ブロック設定を変更できませんでした");
    } finally {
      setBusy(false);
    }
  }

  return <div className="block-action">
    <button type="button" className={blocked ? "secondary" : "danger-outline"} onClick={() => void toggle()} disabled={busy}>
      {busy ? "変更中…" : blocked ? "ブロック解除" : "ブロック"}
    </button>
    {error && <small className="safety-error" role="alert">{error}</small>}
  </div>;
}
